import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { fetchTopicDetail, createPost, togglePostLike, deleteTopic, deletePost, ForumTopicDetail, ForumPost } from '../../services/forumService';
import { useAuth } from '../../context/AuthContext';

const CATEGORY_COLORS: Record<string, string> = {
    GENERAL: '#a78bfa', MACROECO: '#f472b6', BRVM: '#34d399', ACTION_SPECIFIQUE: '#38bdf8',
};
const CATEGORY_LABELS: Record<string, string> = {
    GENERAL: 'Général', MACROECO: 'Macroéconomie', BRVM: 'BRVM', ACTION_SPECIFIQUE: 'Action Spécifique',
};

// Generate deterministic avatar color from name
const getAvatarGradient = (name: string) => {
    const gradients = [
        ['#8b5cf6', '#6366f1'], ['#06b6d4', '#0ea5e9'], ['#f59e0b', '#f97316'],
        ['#10b981', '#059669'], ['#ec4899', '#d946ef'], ['#14b8a6', '#06b6d4'],
        ['#f43f5e', '#e11d48'], ['#6366f1', '#8b5cf6'],
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const idx = Math.abs(hash) % gradients.length;
    return gradients[idx];
};

const ForumTopicScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [topic, setTopic] = useState<ForumTopicDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [replyContent, setReplyContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const { user } = useAuth();

    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (id) loadTopic(parseInt(id));
    }, [id]);

    const loadTopic = async (topicId: number) => {
        try {
            setLoading(true);
            const data = await fetchTopicDetail(topicId);
            setTopic(data);
        } catch (err) {
            console.error("Erreur lors du chargement du sujet", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitReply = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!replyContent.trim() || !topic) return;
        try {
            setSubmitting(true);
            const newPost = await createPost(topic.id, replyContent);
            setTopic((prev: ForumTopicDetail | null) => prev ? {
                ...prev,
                posts: [...prev.posts, newPost],
                replies_count: prev.replies_count + 1
            } : null);
            setReplyContent('');
        } catch (err) {
            console.error("Erreur lors de l'envoi de la réponse", err);
            alert("Une erreur est survenue lors de l'envoi de votre réponse.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleLike = async (postId: number) => {
        if (!topic) return;
        try {
            const result = await togglePostLike(postId);
            setTopic((prev: ForumTopicDetail | null) => prev ? {
                ...prev,
                posts: prev.posts.map(p =>
                    p.id === postId
                        ? { ...p, likes_count: result.likes_count, liked_by_me: result.liked }
                        : p
                )
            } : null);
        } catch (err) {
            console.error('Failed to toggle like', err);
        }
    };

    const handleDeleteTopic = async () => {
        if (!topic || !window.confirm("Voulez-vous vraiment supprimer ce sujet et toutes les réponses ? Cette action est irréversible.")) return;
        try {
            await deleteTopic(topic.id);
            navigate('/forum');
        } catch (err: any) {
            alert(err.message || "Erreur lors de la suppression du sujet");
        }
    };

    const handleDeletePost = async (postId: number) => {
        if (!topic || !window.confirm("Voulez-vous vraiment supprimer ce commentaire ?")) return;
        try {
            await deletePost(postId);
            setTopic(prev => prev ? {
                ...prev,
                posts: prev.posts.filter(p => p.id !== postId),
                replies_count: prev.replies_count - 1
            } : null);
        } catch (err: any) {
            alert(err.message || "Erreur lors de la suppression du commentaire");
        }
    };

    const insertText = (before: string, after: string = '') => {
        if (!textareaRef.current) return;
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const text = textareaRef.current.value;
        const selectedText = text.substring(start, end);
        const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
        setReplyContent(newText);
        
        // Restore focus and selection
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(start + before.length, end + before.length);
            }
        }, 0);
    };

    const renderMarkdown = (text: string) => {
        if (!text) return '';
        // Basic Markdown-ish rendering
        let html = text
            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") // Escape HTML
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
            .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
            .replace(/^-\s+(.*)$/gm, '<li>$1</li>'); // Lists

        // Wrap lists
        if (html.includes('<li>')) {
            html = html.replace(/(<li>.*<\/li>)/gms, '<ul style="margin: 8px 0; padding-left: 20px;">$1</ul>');
        }

        return html;
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return "À l'instant";
        if (diffMin < 60) return `il y a ${diffMin} min`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `il y a ${diffH}h`;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return (
            <SiteLayout>
                <div style={{ padding: '80px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <div style={{ fontSize: '40px', marginBottom: '16px', animation: 'spin 1.5s linear infinite' }}>⏳</div>
                    Chargement du sujet...
                </div>
            </SiteLayout>
        );
    }

    if (!topic) {
        return (
            <SiteLayout>
                <div style={{ padding: '80px', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                    <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '20px' }}>Sujet introuvable</div>
                    <div style={{ color: 'var(--text-muted)', marginTop: '8px' }}>Ce sujet n'existe plus ou a été supprimé.</div>
                    <button onClick={() => navigate('/forum')} style={{
                        marginTop: '24px', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        color: 'white', border: 'none', padding: '10px 24px', borderRadius: '10px',
                        cursor: 'pointer', fontWeight: 700, fontSize: '14px',
                    }}>
                        ← Retour aux Discussions
                    </button>
                </div>
            </SiteLayout>
        );
    }

    const catColor = CATEGORY_COLORS[topic.category] || '#a78bfa';
    const catLabel = CATEGORY_LABELS[topic.category] || topic.category;

    return (
        <SiteLayout>
            <Header icon="forum" title="Sujet de Discussion" />
            <div className='content-area'>
                <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>

                    {/* Back button */}
                    <button
                        onClick={() => navigate('/forum')}
                        style={{
                            background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)',
                            color: 'var(--text-muted)', cursor: 'pointer', marginBottom: '24px',
                            fontWeight: 600, padding: '8px 16px', borderRadius: '10px',
                            fontSize: '13px', transition: 'all 0.2s ease', display: 'inline-flex', alignItems: 'center', gap: '6px',
                        }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = catColor; e.currentTarget.style.color = catColor; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                        ← Retour aux Discussions
                    </button>

                    {/* Topic Header */}
                    <div style={{
                        background: `linear-gradient(135deg, ${catColor}15 0%, transparent 60%)`,
                        borderRadius: '16px', padding: '28px 32px', marginBottom: '28px',
                        border: `1px solid ${catColor}25`,
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{
                            position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px',
                            background: `radial-gradient(circle, ${catColor}20, transparent 70%)`,
                            borderRadius: '50%', pointerEvents: 'none',
                        }} />
                        <h1 style={{
                            color: 'var(--text-main)', fontSize: '26px', margin: '0 0 14px 0',
                            fontWeight: 800, letterSpacing: '-0.3px', lineHeight: 1.3,
                        }}>{topic.title}</h1>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{
                                background: `${catColor}20`, color: catColor,
                                padding: '4px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                            }}>
                                {catLabel}
                            </span>
                            {topic.stock_symbol && (
                                <span style={{
                                    background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8',
                                    padding: '4px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                                    letterSpacing: '0.5px',
                                }}>
                                    📊 {topic.stock_symbol}
                                </span>
                            )}
                            <span style={{ color: 'var(--text-muted)', fontSize: '13px', marginLeft: 'auto' }}>
                                💬 {topic.replies_count} réponses · 👁️ {topic.views_count} vues
                            </span>
                            {user?.id === topic.author_id && (
                                <button
                                    onClick={handleDeleteTopic}
                                    style={{
                                        background: 'rgba(244, 63, 94, 0.1)', border: '1px solid rgba(244, 63, 94, 0.2)',
                                        color: '#f43f5e', padding: '4px 10px', borderRadius: '8px',
                                        fontSize: '11px', fontWeight: 700, cursor: 'pointer', marginLeft: '12px',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(244, 63, 94, 0.2)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(244, 63, 94, 0.1)'}
                                >
                                    🗑️ Supprimer le sujet
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Posts Thread */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', position: 'relative' }}>
                        {/* Vertical Thread Line */}
                        <div style={{
                            position: 'absolute', left: '30px', top: '48px', bottom: '48px', width: '2px',
                            background: `linear-gradient(to bottom, ${catColor}40, var(--border-color), transparent)`,
                            zIndex: 0,
                        }} />

                        {topic.posts.map((post: ForumPost, index: number) => {
                            const grad = getAvatarGradient(post.author_name);
                            const isOpener = post.is_opener;
                            return (
                                <div key={post.id} style={{
                                    display: 'flex', gap: '20px', padding: '24px 0',
                                    position: 'relative', zIndex: 1,
                                    borderBottom: index < topic.posts.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                }}>
                                    {/* Avatar */}
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                                        background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'white', fontWeight: 800, fontSize: '20px',
                                        boxShadow: `0 4px 12px ${grad[0]}44`,
                                    }}>
                                        {post.author_name.charAt(0).toUpperCase()}
                                    </div>

                                    {/* Post Content */}
                                    <div style={{
                                        flex: 1,
                                        background: isOpener ? `${catColor}08` : 'rgba(255,255,255,0.02)',
                                        border: `1px solid ${isOpener ? catColor + '20' : 'var(--border-color)'}`,
                                        borderRadius: '14px',
                                        padding: '20px 24px',
                                        transition: 'all 0.2s ease',
                                    }}>
                                        {/* Author header */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '15px' }}>
                                                    {post.author_name}
                                                </span>
                                                {isOpener && (
                                                    <span style={{
                                                        background: `linear-gradient(135deg, ${catColor}, ${catColor}bb)`,
                                                        color: 'white', padding: '2px 10px', borderRadius: '6px',
                                                        fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                                                    }}>
                                                        Auteur
                                                    </span>
                                                )}
                                            </div>
                                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                                {formatDate(post.created_at)}
                                            </span>
                                        </div>

                                        {/* Content */}
                                        <div 
                                            style={{
                                                color: 'var(--text-main)', lineHeight: 1.7, whiteSpace: 'pre-wrap',
                                                fontSize: '14.5px', letterSpacing: '0.01em',
                                            }}
                                            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
                                        />

                                        {/* Actions */}
                                        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleLike(post.id); }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                    padding: '6px 14px', borderRadius: '20px',
                                                    border: `1px solid ${post.liked_by_me ? '#f43f5e44' : 'var(--border-color)'}`,
                                                    background: post.liked_by_me ? 'rgba(244, 63, 94, 0.1)' : 'rgba(255,255,255,0.02)',
                                                    color: post.liked_by_me ? '#f43f5e' : 'var(--text-muted)',
                                                    cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                }}
                                                onMouseOver={e => { if (!post.liked_by_me) { e.currentTarget.style.borderColor = '#f43f5e66'; e.currentTarget.style.color = '#f43f5e'; } }}
                                                onMouseOut={e => { if (!post.liked_by_me) { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.color = 'var(--text-muted)'; } }}
                                            >
                                                <span style={{ fontSize: '16px', transition: 'transform 0.3s ease', transform: post.liked_by_me ? 'scale(1.2)' : 'scale(1)' }}>
                                                    {post.liked_by_me ? '❤️' : '🤍'}
                                                </span>
                                                {post.likes_count > 0 && <span>{post.likes_count}</span>}
                                            </button>

                                            {user?.id === post.author_id && !post.is_opener && (
                                                <button
                                                    onClick={() => handleDeletePost(post.id)}
                                                    style={{
                                                        background: 'transparent', border: 'none', color: 'var(--text-muted)',
                                                        cursor: 'pointer', fontSize: '12px', padding: '4px 8px', borderRadius: '6px',
                                                        display: 'flex', alignItems: 'center', gap: '4px', opacity: 0.6,
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseOver={e => { e.currentTarget.style.color = '#f43f5e'; e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(244, 63, 94, 0.05)'; }}
                                                    onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                    🗑️ Supprimer
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Reply Section */}
                    <div style={{
                        marginTop: '32px',
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(168, 85, 247, 0.04) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.12)',
                        borderRadius: '16px', padding: '28px',
                    }}>
                        <h3 style={{
                            color: 'var(--text-main)', marginTop: 0, marginBottom: '18px',
                            fontWeight: 700, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px',
                        }}>
                            ✍️ Votre réponse
                        </h3>
                        <form onSubmit={handleSubmitReply}>
                        <div style={{ position: 'relative' }}>
                            {/* Toolbar */}
                            <div style={{
                                display: 'flex', gap: '4px', marginBottom: '8px',
                                padding: '4px', borderRadius: '8px',
                                background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)',
                            }}>
                                {[
                                    { label: 'B', tag: '**', after: '**', title: 'Gras' },
                                    { label: 'I', tag: '*', after: '*', title: 'Italique' },
                                    { label: '•', tag: '- ', after: '', title: 'Liste' },
                                ].map((tool, i) => (
                                    <button
                                        key={i} type="button"
                                        onClick={() => insertText(tool.tag, tool.after)}
                                        title={tool.title}
                                        style={{
                                            width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                                            background: 'transparent', color: 'var(--text-main)',
                                            cursor: 'pointer', fontWeight: 800, fontSize: '13px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        {tool.label}
                                    </button>
                                ))}
                                <div style={{ width: '1px', background: 'var(--border-color)', margin: '4px 2px' }} />
                                <button
                                    type="button"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    title="Emojis"
                                    style={{
                                        width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                                        background: showEmojiPicker ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                                        color: 'var(--text-main)', cursor: 'pointer', fontSize: '16px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = showEmojiPicker ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)'}
                                    onMouseOut={e => e.currentTarget.style.background = showEmojiPicker ? 'rgba(139, 92, 246, 0.2)' : 'transparent'}
                                >
                                    😊
                                </button>

                                {showEmojiPicker && (
                                    <div style={{
                                        position: 'absolute', bottom: '100%', left: '0', marginBottom: '10px',
                                        background: '#1e293b', border: '1px solid var(--border-color)',
                                        borderRadius: '12px', padding: '12px', display: 'grid',
                                        gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.5)', zIndex: 10,
                                    }}>
                                        {['📈', '📉', '💰', '🚀', '⚠️', '🔥', '👍', '🙏', '🤝', '💎', '🧐', '💡', '📊', '📅', '💬', '😊', '😭', '🤯'].map(emoji => (
                                            <button
                                                key={emoji} type="button"
                                                onClick={() => { insertText(emoji); setShowEmojiPicker(false); }}
                                                style={{
                                                    fontSize: '20px', background: 'transparent', border: 'none',
                                                    cursor: 'pointer', padding: '4px', borderRadius: '6px',
                                                }}
                                                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <textarea
                                ref={textareaRef}
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                placeholder="Partagez votre analyse, votre avis ou posez une question..."
                                disabled={submitting}
                                required
                                style={{
                                    width: '100%', minHeight: '130px', padding: '16px',
                                    backgroundColor: 'rgba(0,0,0,0.15)',
                                    color: 'var(--text-main)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '12px', marginBottom: '16px',
                                    resize: 'vertical', fontFamily: 'inherit',
                                    fontSize: '14px', lineHeight: 1.6,
                                    transition: 'border-color 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={e => e.currentTarget.style.borderColor = '#8b5cf6'}
                                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                            />
                        </div>
                            <button
                                type="submit"
                                disabled={submitting || !replyContent.trim()}
                                style={{
                                    background: submitting || !replyContent.trim()
                                        ? 'rgba(99, 102, 241, 0.3)'
                                        : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                                    color: 'white', border: 'none',
                                    padding: '12px 28px', borderRadius: '12px',
                                    cursor: submitting || !replyContent.trim() ? 'not-allowed' : 'pointer',
                                    fontWeight: 700, fontSize: '14px',
                                    boxShadow: submitting || !replyContent.trim() ? 'none' : '0 4px 15px rgba(99, 102, 241, 0.35)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                }}
                                onMouseOver={e => { if (!submitting && replyContent.trim()) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.5)'; } }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(99, 102, 241, 0.35)'; }}
                            >
                                {submitting ? '⏳ Envoi en cours...' : '📨 Envoyer la réponse'}
                            </button>
                        </form>
                    </div>

                </div>
            </div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </SiteLayout>
    );
};

export default ForumTopicScreen;
